import type { ReactNode } from 'react';
import type React from 'react';
import { useAppSelector } from '../../hooks';
import { selectIsMobile } from 'reducers/main';
import { styled, useTheme } from '@mui/material/styles';
import { Typography } from '@forethought-technologies/forethought-elements';
import { Box, IconButton } from '@mui/material';
import { IconX } from '@tabler/icons-react';

// Utils

interface NotificationToastProps {
  /** Message to display */
  content: ReactNode;
  /** Callback to call when the toast is dismissed */
  onDismiss?: () => void;
  /** Text color for the component */
  title: string;
}

const NotificationToast: React.FC<
  React.PropsWithChildren<NotificationToastProps>
> = ({ content, onDismiss, title }) => {
  const theme = useTheme();

  const isMobile = useAppSelector(selectIsMobile);

  return (
    <Container isMobile={isMobile}>
      {!!onDismiss && (
        <Box
          sx={{
            position: 'absolute',
            right: '8px',
            top: '8px',
          }}
        >
          <IconButton
            aria-label='Dismiss notification toast'
            onClick={onDismiss}
            sx={{ padding: 0 }}
          >
            <IconX color={theme.palette.colors.grey[700]} size={15} />
          </IconButton>
        </Box>
      )}
      <Typography variant='font14Bold'>{title}</Typography>
      <Typography color={theme.palette.colors.grey[600]} variant='font12'>
        {content}
      </Typography>
    </Container>
  );
};

export default NotificationToast;

const Container = styled('div')<{ isMobile: boolean }>`
  position: absolute;
  top: 100px;
  left: 22px;
  right: 22px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: white;
  color: ${props => props.color};
  padding: 16px;
  z-index: 4;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-left: 10px solid ${props => props.theme.palette.warning.main};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  cursor: default;
  height: fit-content;
  ${props =>
    props.isMobile &&
    `
      left: 8px;
      right: 8px;
      top: 75px;
    `}
`;
